/**
 * useFavoriteProducts Hook
 *
 * Combines useFavorites with Catalog API to fetch actual product data
 * for favorites display on Core Frontend /favorites page.
 *
 * @see HDR-14: Core Frontend Favorites - Catalog Integration
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFavorites, type UseFavoritesOptions } from './useFavorites';
import { catalogAPI } from '@/lib/api/catalog';
import { coreAPI_Service } from '@/lib/api/core';
import type { SearchProduct } from '@/types/catalog';
import type { FavoriteItem } from '@/lib/api/favorites';

/**
 * Combined favorite + catalog product data
 */
export interface FavoriteProduct {
  /** Original favorite item from Core Backend */
  favorite: FavoriteItem;
  /** Product data from Catalog (null if not found) */
  product: SearchProduct | null;
  /** Whether product is available in Catalog */
  isAvailable: boolean;
  /** Resolved merchant business_name (from batch lookup) */
  merchantName?: string;
}

/**
 * Return type for useFavoriteProducts hook
 */
export interface UseFavoriteProductsResult {
  /** All favorite products (available and unavailable) */
  products: FavoriteProduct[];
  /** Products found in Catalog */
  availableProducts: FavoriteProduct[];
  /** Products NOT found in Catalog (deleted/unpublished) */
  unavailableProducts: FavoriteProduct[];
  /** GUIDs of products not found in Catalog */
  notFoundGuids: string[];
  /** Loading state (favorites OR catalog) */
  isLoading: boolean;
  /** Error from catalog fetch */
  error: Error | null;
  /** Remove unavailable product from favorites */
  removeUnavailable: (entityId: string) => Promise<void>;
  /** Refetch both favorites and catalog data */
  refetch: () => void;
}

/**
 * Hook for fetching favorites with actual product data from Catalog
 *
 * Solves the problem where favorites added from Storefront have minimal metadata,
 * resulting in "Producto" with price 0 on Core Frontend.
 *
 * @example
 * ```tsx
 * function FavoritesList() {
 *   const {
 *     availableProducts,
 *     unavailableProducts,
 *     isLoading,
 *     removeUnavailable,
 *   } = useFavoriteProducts();
 *
 *   if (isLoading) return <Loader />;
 *
 *   return (
 *     <>
 *       {availableProducts.map(({ product }) => (
 *         <ProductCard key={product.product_guid} product={product} />
 *       ))}
 *       {unavailableProducts.map(({ favorite }) => (
 *         <UnavailableProductCard
 *           key={favorite.entity_id}
 *           onRemove={() => removeUnavailable(favorite.entity_id)}
 *         />
 *       ))}
 *     </>
 *   );
 * }
 * ```
 */
export function useFavoriteProducts(
  options: Omit<UseFavoritesOptions, 'type'> = {}
): UseFavoriteProductsResult {
  const {
    favorites,
    isLoading: favLoading,
    removeFavorite,
    refetch: refetchFavorites,
  } = useFavorites({
    type: 'product',
    ...options,
  });

  // Extract product GUIDs from favorites
  const productGuids = useMemo(
    () => favorites.map((f) => f.entity_id),
    [favorites]
  );

  // Fetch product data from Catalog
  const {
    data: catalogData,
    isLoading: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useQuery({
    queryKey: ['catalog-products-batch', productGuids],
    queryFn: () => catalogAPI.getProductsByGuids(productGuids),
    enabled: productGuids.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract unique merchant GUIDs from catalog products
  const merchantGuids = useMemo(() => {
    if (!catalogData?.products) return [];
    const guids = new Set(
      catalogData.products.map((p) => p.merchant_guid).filter(Boolean)
    );
    return Array.from(guids);
  }, [catalogData]);

  // Fetch merchant names (batch lookup)
  const { data: merchants, isLoading: merchantsLoading } = useQuery({
    queryKey: ['merchants-batch', merchantGuids],
    queryFn: () => coreAPI_Service.getMerchantsByGuids(merchantGuids),
    enabled: merchantGuids.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes - merchant names rarely change
  });

  // Create merchant lookup map
  const merchantMap = useMemo(() => {
    if (!merchants) return new Map<string, string>();
    return new Map(merchants.map((m) => [m.merchant_guid, m.business_name]));
  }, [merchants]);

  // Merge favorites with catalog data and merchant names
  const products = useMemo((): FavoriteProduct[] => {
    if (!catalogData) {
      // Return favorites without catalog data while loading
      return favorites.map((fav) => ({
        favorite: fav,
        product: null,
        isAvailable: false,
      }));
    }

    return favorites.map((fav) => {
      const catalogProduct = catalogData.products.find(
        (p) => p.product_guid === fav.entity_id
      );
      return {
        favorite: fav,
        product: catalogProduct ?? null,
        isAvailable: !!catalogProduct,
        merchantName: catalogProduct
          ? merchantMap.get(catalogProduct.merchant_guid)
          : undefined,
      };
    });
  }, [favorites, catalogData, merchantMap]);

  // Filter helpers
  const availableProducts = useMemo(
    () => products.filter((p) => p.isAvailable),
    [products]
  );

  const unavailableProducts = useMemo(
    () => products.filter((p) => !p.isAvailable),
    [products]
  );

  // Handle remove unavailable
  const removeUnavailable = useCallback(
    async (entityId: string) => {
      await removeFavorite({ entityType: 'product', entityId });
    },
    [removeFavorite]
  );

  // Combined refetch
  const refetch = useCallback(() => {
    refetchFavorites();
    refetchCatalog();
  }, [refetchFavorites, refetchCatalog]);

  return {
    products,
    availableProducts,
    unavailableProducts,
    notFoundGuids: catalogData?.not_found ?? [],
    isLoading: favLoading || catalogLoading || merchantsLoading,
    error: catalogError as Error | null,
    removeUnavailable,
    refetch,
  };
}
